import { deriveCheckInStatus } from "@sunfish/api/check-in/derive-status";
import type { CheckInEvent, DerivedCheckInStatus } from "@sunfish/api/check-in/types";
import { getDb } from "./db";
import type { CachedAttendee, LocalCheckIn } from "./db";

export function filterAttendees(rows: CachedAttendee[], query: string): CachedAttendee[] {
	const q = query.trim().toLowerCase();
	if (!q) return rows;
	return rows.filter((r) => r.search.includes(q));
}

export function toCheckInEvent(c: LocalCheckIn): CheckInEvent {
	return {
		clientId: c.clientId,
		direction: c.direction,
		voidsClientId: c.voidsClientId,
		occurredAt: c.occurredAt,
		createdAt: c.createdAt,
	};
}

export function statusFor(attendeeId: string, all: LocalCheckIn[]): DerivedCheckInStatus {
	return deriveCheckInStatus(all.filter((c) => c.attendeeId === attendeeId).map(toCheckInEvent));
}

export function mergeCheckIns(existing: LocalCheckIn[], incoming: LocalCheckIn[]): LocalCheckIn[] {
	const byId = new Map(existing.map((c) => [c.clientId, c]));
	for (const inc of incoming) {
		const prev = byId.get(inc.clientId);
		byId.set(inc.clientId, prev ? { ...inc, synced: (prev.synced || inc.synced) as 0 | 1 } : inc);
	}
	return [...byId.values()];
}

export function buildSearchKey(a: {
	firstName: string | null;
	lastName: string | null;
	email: string | null;
}): string {
	return `${a.firstName ?? ""} ${a.lastName ?? ""} ${a.email ?? ""}`.toLowerCase();
}

export async function cacheRoster(
	eventId: string,
	attendees: Array<Omit<CachedAttendee, "search">>,
): Promise<void> {
	const db = getDb(eventId);
	await db.attendees.bulkPut(attendees.map((a) => ({ ...a, search: buildSearchKey(a) })));
}

export async function searchRoster(eventId: string, query: string): Promise<CachedAttendee[]> {
	const all = await getDb(eventId).attendees.toArray();
	return filterAttendees(all, query).slice(0, 25);
}

export async function getAttendee(
	eventId: string,
	attendeeId: string,
): Promise<CachedAttendee | undefined> {
	return getDb(eventId).attendees.get(attendeeId);
}

export async function getAttendeeByCode(
	eventId: string,
	code: string,
): Promise<CachedAttendee | undefined> {
	return getDb(eventId).attendees.where("confirmationCode").equals(code.trim()).first();
}

export async function enqueueCheckIn(eventId: string, row: LocalCheckIn): Promise<void> {
	await getDb(eventId).checkIns.put(row);
}

export async function getCheckInsFor(
	eventId: string,
	attendeeId: string,
): Promise<LocalCheckIn[]> {
	return getDb(eventId).checkIns.where("attendeeId").equals(attendeeId).toArray();
}

export async function getOutbox(eventId: string): Promise<LocalCheckIn[]> {
	return getDb(eventId).checkIns.where("synced").equals(0).toArray();
}

export async function applyRemote(eventId: string, incoming: LocalCheckIn[]): Promise<void> {
	const db = getDb(eventId);
	await db.transaction("rw", db.checkIns, async () => {
		for (const inc of incoming) {
			const prev = await db.checkIns.get(inc.clientId);
			await db.checkIns.put(prev ? { ...inc, synced: (prev.synced || inc.synced) as 0 | 1 } : inc);
		}
	});
}

export async function markSynced(eventId: string, clientIds: string[]): Promise<void> {
	const db = getDb(eventId);
	await db.transaction("rw", db.checkIns, async () => {
		for (const id of clientIds) {
			const row = await db.checkIns.get(id);
			if (row) await db.checkIns.put({ ...row, synced: 1 });
		}
	});
}

export async function getMeta(eventId: string, key: string): Promise<string | undefined> {
	return (await getDb(eventId).meta.get(key))?.value;
}

export async function setMeta(eventId: string, key: string, value: string): Promise<void> {
	await getDb(eventId).meta.put({ key, value });
}
